import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { checkGraphqlAccess } from '../service/networkRestriction';
import type { GraphqlAccess } from '../service/networkRestriction';

const PROBE_KEY = 'networkAccessProbe';

export interface UseNetworkRestrictionArgs {
  enabled: boolean;
}

export interface UseNetworkRestrictionResult {
  isRestricted: boolean;
  isChecking: boolean;
  retryFailed: boolean;
  retry: () => Promise<void>;
}

/**
 * Access probe state (feature 041). Re-runs on refocus after 30 s and on reconnect;
 * when access comes back it invalidates every other query so the portal reloads its data.
 */
export default function useNetworkRestriction({ enabled }: UseNetworkRestrictionArgs): UseNetworkRestrictionResult {
  const queryClient = useQueryClient();
  const [retryFailed, setRetryFailed] = useState(false);
  const previous = useRef<GraphqlAccess | undefined>(undefined);
  const mounted = useRef(true);

  const { data, isFetching, refetch } = useQuery([PROBE_KEY], checkGraphqlAccess, {
    enabled,
    retry: false,
    staleTime: 30000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (data === undefined) return;
    if (previous.current === 'restricted' && data === 'allowed') {
      queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] !== PROBE_KEY });
    }
    previous.current = data;
  }, [data, queryClient]);

  const retry = useCallback(async () => {
    setRetryFailed(false);
    const result = await refetch();
    if (mounted.current) setRetryFailed(result.data === 'restricted');
  }, [refetch]);

  return { isRestricted: data === 'restricted', isChecking: isFetching, retryFailed, retry };
}
